import {
    KeyboardArrowDown as ArrowDownIcon,
    Close as CloseIcon,
    NorthEast as NorthEastIcon,
    Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { Avatar, Box, ButtonBase, Card, CardContent, CircularProgress, CssBaseline, Divider, IconButton, List, ListItem, Menu, MenuItem, Modal, Popover, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCurrentProfile } from '../../ContextWrapper';
import { get } from '../../api/api';
import { fetchVendors } from '../../api/vendors';
import { handlePerformanceYtdToggle } from '../../utils';
import Sidebar from '../dashboard/sideNav';

const theme = createTheme();

const PRIMARY = '#536DEC';
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Returns the last day of a given month */
const lastDayOfMonth = (year, monthIdx) => new Date(year, monthIdx + 1, 0);

/** "Jan 2026" style label */
const formatMonthLabel = (date) => {
    const yr = date.getFullYear();
    const mo = date.toLocaleString('en-US', { month: 'short' });
    return `${mo} ${yr}`;
};

/** Single-month picker shown as a Popover below the Month toggle button */
function MonthPickerContent({ selectedDate, onSelect }) {
    const today = new Date();
    const [displayYear, setDisplayYear] = useState(
        selectedDate ? selectedDate.getFullYear() : today.getFullYear()
    );

    const isDisabled = (year, monthIdx) => {
        // Disable the current month and any future month
        return (
            year > today.getFullYear() ||
            (year === today.getFullYear() && monthIdx >= today.getMonth())
        );
    };

    const isSelected = (year, monthIdx) => {
        if (!selectedDate) return false;
        return (
            selectedDate.getFullYear() === year &&
            selectedDate.getMonth() === monthIdx
        );
    };

    return (
        <Box sx={{ p: 1.5, minWidth: 220 }}>
            {/* Year navigation */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Box
                    onClick={() => setDisplayYear((y) => y - 1)}
                    sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#F3F4F6' }, display: 'flex' }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(0,0,0,0.65)">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                    </svg>
                </Box>
                <Typography sx={{ fontFamily: "'Rethink Sans', sans-serif", fontSize: 14, fontWeight: 600, minWidth: 36, textAlign: 'center' }}>
                    {displayYear}
                </Typography>
                <Box
                    onClick={() => displayYear < today.getFullYear() && setDisplayYear((y) => y + 1)}
                    sx={{
                        cursor: displayYear < today.getFullYear() ? 'pointer' : 'default',
                        p: 0.5,
                        borderRadius: 1,
                        opacity: displayYear >= today.getFullYear() ? 0.3 : 1,
                        '&:hover': displayYear < today.getFullYear() ? { bgcolor: '#F3F4F6' } : {},
                        display: 'flex',
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(0,0,0,0.65)">
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                    </svg>
                </Box>
            </Stack>

            {/* Month grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                {MONTH_NAMES.map((name, idx) => {
                    const disabled = isDisabled(displayYear, idx);
                    const selected = isSelected(displayYear, idx);
                    return (
                        <Box
                            key={name}
                            onClick={() => !disabled && onSelect(displayYear, idx)}
                            sx={{
                                textAlign: 'center',
                                py: 0.875,
                                borderRadius: '6px',
                                cursor: disabled ? 'default' : 'pointer',
                                border: selected ? `1px solid ${PRIMARY}` : '1px solid rgba(0,0,0,0.12)',
                                bgcolor: selected ? PRIMARY : 'transparent',
                                color: selected ? '#fff' : disabled ? 'rgba(0,0,0,0.26)' : 'rgba(0,0,0,0.75)',
                                fontFamily: "'Rethink Sans', sans-serif",
                                fontSize: 13,
                                fontWeight: selected ? 700 : 400,
                                transition: 'background 0.12s',
                                '&:hover': !disabled ? {
                                    bgcolor: selected ? PRIMARY : 'rgba(83,109,236,0.14)',
                                    borderColor: selected ? PRIMARY : 'rgba(83,109,236,0.4)',
                                } : {},
                            }}
                        >
                            {name}
                        </Box>
                    );
                })}
            </Box>

            <Typography sx={{ fontFamily: "'Rethink Sans', sans-serif", fontSize: 11, color: 'rgba(0,0,0,0.38)', mt: 1.25, textAlign: 'center' }}>
                {selectedDate
                    ? `${selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} selected`
                    : 'Select a completed month'}
            </Typography>
        </Box>
    );
}

// Shared search input styles (header trigger + modal input)
const SEARCH_INPUT_PLACEHOLDER = 'Search...';
const searchInputPropsSx = {
    bgcolor: '#F1F2F7',
    borderRadius: 2,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D4DA' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BFC3CC' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3B82F6' },
    '& .MuiInputBase-input::placeholder': {
        color: 'var(--text-disabled, rgba(0, 0, 0, 0.38))',
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: 'var(--fontFamily, "Rethink Sans")',
        fontSize: 'var(--_fontSize-1rem, 16px)',
        fontStyle: 'normal',
        fontWeight: 'var(--fontWeightRegular, 400)',
        lineHeight: '150%',
        letterSpacing: '0.15px',
        opacity: 1,
    },
};
// Modal search input: no background so it matches the header
const searchModalInputPropsSx = {
    bgcolor: 'transparent',
    borderRadius: 0,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent', borderWidth: 0 },
    '& .MuiInputBase-input::placeholder': {
        color: 'var(--text-disabled, rgba(0, 0, 0, 0.38))',
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: 'var(--fontFamily, "Rethink Sans")',
        fontSize: 'var(--_fontSize-1rem, 16px)',
        fontStyle: 'normal',
        fontWeight: 'var(--fontWeightRegular, 400)',
        lineHeight: '150%',
        letterSpacing: '0.15px',
        opacity: 1,
    },
};

export default function ClientDashboardLayout({
    children,
    title,
    contentMaxWidth = 1400,
    contentAlign = 'center',
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { customer_id: routeCustomerId } = useParams();
    const { profile, fireToast } = useCurrentProfile();

    const [selectedCustomerId, setSelectedCustomerId] = useState(() => {
        try {
            return sessionStorage.getItem('selectedCustomerId');
        } catch (error) {
            return null;
        }
    });

    useEffect(() => {
        const handleSelectedCustomerIdChanged = () => {
            try {
                setSelectedCustomerId(sessionStorage.getItem('selectedCustomerId'));
            } catch (error) {
                setSelectedCustomerId(null);
            }
        };

        window.addEventListener('selectedCustomerId-changed', handleSelectedCustomerIdChanged);
        return () => {
            window.removeEventListener('selectedCustomerId-changed', handleSelectedCustomerIdChanged);
        };
    }, []);

    const effectiveCustomerId = useMemo(() => {
        if (routeCustomerId) {
            return routeCustomerId;
        }

        if (selectedCustomerId && selectedCustomerId !== 'null') {
            return selectedCustomerId;
        }

        return profile?.customers?.[0]?.id ?? null;
    }, [profile?.customers, routeCustomerId, selectedCustomerId]);

    useEffect(() => {
        if (!routeCustomerId) {
            return;
        }

        try {
            sessionStorage.setItem('selectedCustomerId', String(routeCustomerId));
            window.dispatchEvent(new Event('selectedCustomerId-changed'));
        } catch (error) {
            // ignore
        }
    }, [routeCustomerId]);

    const [userData, setUserData] = useState(null);
    const [sidebarExpanded, setSidebarExpanded] = useState(true);
    const isMobile = useMediaQuery('(max-width:1280px)', { noSsr: true });
    const isPerformanceRoute = location?.pathname?.startsWith('/performance');
    const [isYtd, setIsYtd] = useState(() => {
        try {
            const stored = sessionStorage.getItem('performanceYtd');
            if (stored === null) return true; // default ON (YTD) for client dashboard
            return stored === 'true';
        } catch (error) {
            return true;
        }
    });

    // Selected month for the "Month" tab – defaults to the last completed month
    const [selectedMonthDate, setSelectedMonthDate] = useState(() => {
        try {
            const stored = sessionStorage.getItem('performanceSelectedMonth');
            if (stored) {
                const parsed = new Date(stored);
                if (!isNaN(parsed)) return parsed;
            }
        } catch { /* ignore */ }
        const now = new Date();
        return lastDayOfMonth(now.getFullYear(), now.getMonth() - 1);
    });

    const [monthPickerOpen, setMonthPickerOpen] = useState(false);
    const [monthPickerAnchor, setMonthPickerAnchor] = useState(null);

    const selectedMonthLabel = useMemo(() => formatMonthLabel(selectedMonthDate), [selectedMonthDate]);

    const [ytdDisabled, setYtdDisabled] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [vendorResults, setVendorResults] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [categoryResults, setCategoryResults] = useState([]);
    const [bankAccountIds, setBankAccountIds] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [modalSearchQuery, setModalSearchQuery] = useState('');

    // Debounced search effect for modal search
    useEffect(() => {
        if (!modalSearchQuery.trim()) {
            setSearchResults([]);
            setVendorResults([]);
            setCategoryResults([]);
            setIsSearching(false);
            return;
        }

        const customerId = effectiveCustomerId;
        if (!customerId) return;

        const abortController = new AbortController();
        setIsSearching(true);

        const timeoutId = setTimeout(() => {
            // Search transactions and vendors; filter categories client-side
            const query = modalSearchQuery.toLowerCase();
            const filteredCategories = allCategories.filter(
                (c) => c.name.toLowerCase().includes(query) || (c.number && c.number.toLowerCase().includes(query))
            );
            setCategoryResults(filteredCategories.slice(0, 5)); // Top 5 categories

            // Search transactions across all bank accounts
            const txPromises = bankAccountIds.map((bankAccountId) =>
                axios.get(`/api/v2/bankaccounts/${bankAccountId}/transactions/`, {
                    params: {
                        search: modalSearchQuery,
                        ordering: '-date',
                        with_classification: true,
                        include_journal_entries: false,
                        customer_id: customerId,
                    },
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                    signal: abortController.signal,
                }).catch(err => {
                    if (err.name === 'AbortError' || err.name === 'CanceledError') {
                        return { data: { results: [] } };
                    }
                    console.error("Error searching transactions:", err);
                    return { data: { results: [] } };
                })
            );

            Promise.all([
                ...txPromises,
                fetchVendors({ name__icontains: modalSearchQuery, customer_id: customerId }, { signal: abortController.signal }).catch(err => {
                    if (err.name === 'AbortError') {
                        return { results: [] };
                    }
                    console.error("Error searching vendors:", err);
                    return { results: [] };
                })
            ])
                .then((responses) => {
                    if (abortController.signal.aborted) return;

                    const vendorResponse = responses[responses.length - 1];
                    const txResponses = responses.slice(0, -1);

                    // Merge transactions from all bank accounts
                    const allTx = [];
                    txResponses.forEach((res) => {
                        const data = res?.data?.results ?? res?.data ?? [];
                        const items = Array.isArray(data) ? data : [];
                        allTx.push(...items);
                    });
                    // Sort by date descending and deduplicate by id
                    const seen = new Set();
                    const uniqueTx = allTx
                        .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                        .filter((tx) => {
                            if (seen.has(tx.id)) return false;
                            seen.add(tx.id);
                            return true;
                        });

                    const vendorData = Array.isArray(vendorResponse?.results)
                        ? vendorResponse.results
                        : Array.isArray(vendorResponse)
                        ? vendorResponse
                        : [];

                    setSearchResults(uniqueTx.slice(0, 5)); // Top 5 transactions
                    setVendorResults(vendorData.slice(0, 3)); // Top 3 vendors
                    setIsSearching(false);
                })
                .catch((error) => {
                    if (error.name === 'AbortError' || error.name === 'CanceledError' || abortController.signal.aborted) {
                        return;
                    }
                    console.error("Error searching:", error);
                    setSearchResults([]);
                    setVendorResults([]);
                    setCategoryResults([]);
                    setIsSearching(false);
                });
        }, 500); // 500ms debounce

        return () => {
            clearTimeout(timeoutId);
            abortController.abort();
        };
    }, [modalSearchQuery, effectiveCustomerId, allCategories, bankAccountIds]);

    // Fetch all categories (accounts) and bank account IDs once for search
    useEffect(() => {
        const customerId = effectiveCustomerId;
        if (!customerId) return;

        axios.get(`/api/v2/customers/${customerId}/accounts/`, {
            params: { namespace: 'new', leaf_only: true },
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
        })
            .then((res) => {
                const data = Array.isArray(res?.data?.results)
                    ? res.data.results
                    : Array.isArray(res?.data)
                    ? res.data
                    : [];
                setAllCategories(
                    data.filter((a) => a && a.id && a.name).map((a) => ({
                        id: a.id,
                        name: a.name,
                        number: a.number ?? '',
                    }))
                );
            })
            .catch((err) => {
                console.error('Error fetching categories:', err);
                setAllCategories([]);
            });

        axios.get(`/api/v2/customers/${customerId}/plaiditems`, {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
        })
            .then((res) => {
                const plaidItems = Array.isArray(res.data) ? res.data : [];
                const ids = [];
                plaidItems.forEach((item) => {
                    (item.bankaccount_set || [])
                        .filter((account) => account.accounting_account)
                        .forEach((account) => ids.push(account.id));
                });
                setBankAccountIds(ids);
            })
            .catch((err) => {
                console.error('Error fetching plaid items for search:', err);
                setBankAccountIds([]);
            });
    }, [effectiveCustomerId]);

    // Fetch user data when profile is available
    useEffect(() => {
        if (!profile?.id) return;
        let active = true;

        const normalizeMediaUrl = (url) => {
            if (!url || typeof url !== "string") return "";
            const trimmed = url.trim();
            if (!trimmed) return "";
            if (process.env.NODE_ENV === "development" && trimmed.startsWith("http")) {
                try {
                    const parsed = new URL(trimmed);
                    const host = parsed.hostname.toLowerCase();
                    const isDevBackend = host === "backend" || host === "localhost" || host === "127.0.0.1";
                    if (isDevBackend) return parsed.pathname;
                } catch {
                    const match = trimmed.match(/^https?:\/\/(backend|localhost|127\.0\.0\.1)(?::\d+)?(\/.*)$/);
                    if (match) return match[2];
                }
            }
            return trimmed;
        };

        const loadUserData = () => {
            get(`/api/v2/profiles/${profile.id}/`)
                .then((fullProfile) => {
                    if (!active) return;
                    setUserData({
                        ...(fullProfile?.user || {}),
                        profile_picture: normalizeMediaUrl(fullProfile?.profile_picture) || "",
                    });
                })
                .catch((error) => {
                    console.error("Error fetching user data:", error);
                });
        };

        loadUserData();

        const onProfileUpdated = () => loadUserData();
        window.addEventListener("profile-updated", onProfileUpdated);

        return () => {
            active = false;
            window.removeEventListener("profile-updated", onProfileUpdated);
        };
    }, [profile?.id]);

    // State for notification popover
    const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
    const notificationOpen = Boolean(notificationAnchorEl);

    // State for profile menu
    const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
    const profileMenuOpen = Boolean(profileMenuAnchorEl);

    // Notification bell handlers
    const handleNotificationClick = (event) => {
        setNotificationAnchorEl(event.currentTarget);
    };

    const handleNotificationClose = () => {
        setNotificationAnchorEl(null);
    };

    // Profile menu handlers
    const handleProfileMenuClick = (event) => {
        setProfileMenuAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setProfileMenuAnchorEl(null);
    };

    const handleProfileSettings = () => {
        handleProfileMenuClose();
        navigate("/profile-settings");
    };

    const handleLogout = () => {
        handleProfileMenuClose();
        navigate("/logout");
    };

    const getDisplayName = () => {
        if (userData?.first_name || userData?.last_name) {
            return `${userData.first_name || ""} ${userData.last_name || ""}`.trim();
        }
        if (userData?.email) {
            const email = userData.email;
            const nameFromEmail = email.split("@")[0];
            return nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1);
        }
        return "User";
    };

    const getUserTypeDisplay = () => {
        const type = profile?.type;
        switch (type) {
            case "admin": return "Lucro Admin";
            case "accountant": return "Lucro Accountant";
            case "client": return "Lucro Customer";
            default: return "Lucro User";
        }
    };

    const getUserInitials = () => {
        const firstName = userData?.first_name || "";
        const lastName = userData?.last_name || "";
        if (firstName && lastName) {
            return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        }
        if (firstName) return firstName.charAt(0).toUpperCase();
        if (userData?.email) return userData.email.charAt(0).toUpperCase();
        return "U";
    };

    const handleYtdSelect = (next) => {
        if (ytdDisabled) return;
        setYtdDisabled(true);

        setIsYtd(next);
        handlePerformanceYtdToggle(next);

        setTimeout(() => setYtdDisabled(false), 500);
    };

    const handleMonthSelect = (year, monthIdx) => {
        const endDate = lastDayOfMonth(year, monthIdx);
        setSelectedMonthDate(endDate);
        setMonthPickerOpen(false);
        const formatDateLocal = (date) => date.toLocaleDateString("sv-SE");
        try {
            sessionStorage.setItem('performanceSelectedMonth', formatDateLocal(endDate));
        } catch { /* ignore */ }
        if (isYtd) {
            // Also switch to Month mode
            handleYtdSelect(false);
        } else {
            // Already on Month mode – just dispatch so performanceOverview re-reads the month
            window.dispatchEvent(new Event('performance-ytd-changed'));
        }
    };

    const handleMonthTabClick = (e) => {
        // Always open the month picker when clicking the Month tab
        setMonthPickerAnchor(e.currentTarget);
        setMonthPickerOpen(true);
        if (isYtd) {
            handleYtdSelect(false);
        }
    };

    useEffect(() => {
        const syncFromStorage = () => {
            try {
                const stored = sessionStorage.getItem('performanceYtd');
                if (stored === null) {
                    setIsYtd(true);
                    return;
                }
                setIsYtd(stored === 'true');
            } catch (error) {
                setIsYtd(true);
            }
        };

        window.addEventListener('performance-ytd-changed', syncFromStorage);
        return () => window.removeEventListener('performance-ytd-changed', syncFromStorage);
    }, []);

    const handleCloseSearchModal = () => {
        setSearchModalOpen(false);
        setModalSearchQuery('');
        setSearchResults([]);
        setVendorResults([]);
        setIsSearching(false);
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />

            <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F9FAFB', minWidth: 0 }}>
                <Sidebar
                    expanded={sidebarExpanded}
                    onToggle={setSidebarExpanded}
                    customers={profile?.customers}
                    profile={profile}
                />

                <Box
                    sx={{
                        flexGrow: 1,
                        ml: isMobile ? 0 : sidebarExpanded ? '300px' : '94px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'margin-left 0.2s ease',
                        minWidth: 0,
                    }}
                >
                    {/* Top Bar: fullscreen = search | toggle | profile (right). Narrow = row1 search+profile, row2 toggle.
                        DOM order: search, profile, toggle so that toggle (100% width on xs) wraps to row 2; order used for fullscreen. */}
                    <Box
                        sx={{
                            bgcolor: '#FFFFFF',
                            borderBottom: '1px solid #E2E6EF',
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            px: { xs: 2, sm: 3 },
                            py: 2,
                            position: 'sticky',
                            top: 0,
                            zIndex: 1100,
                            boxShadow: '0 4px 10px rgba(15,23,42,0.06)',
                        }}
                    >
                        {/* 1. Search (left; order 1 so fullscreen shows first) */}
                        <Box sx={{ flex: '1 1 0', minWidth: 0, order: 1 }}>
                            <TextField
                                value={modalSearchQuery}
                                onClick={() => setSearchModalOpen(true)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setSearchModalOpen(true);
                                    }
                                }}
                                placeholder={SEARCH_INPUT_PLACEHOLDER}
                                size="small"
                                sx={{ width: '100%', maxWidth: 441, cursor: 'pointer' }}
                                InputProps={{
                                    readOnly: true,
                                    startAdornment: (
                                        <Box
                                            component="img"
                                            src="/search.svg"
                                            alt=""
                                            sx={{ width: 20, height: 20, mr: 1 }}
                                        />
                                    ),
                                    sx: { ...searchInputPropsSx, cursor: 'pointer' },
                                }}
                            />
                        </Box>

                        {/* 2. Notification + profile (DOM second so they stay on row 1 when toggle wraps; order 3 on md = far right) */}
                        <Stack
                            direction="row"
                            spacing={2.5}
                            alignItems="center"
                            sx={{ flexShrink: 0, order: { xs: 2, md: 3 } }}
                        >
                            <IconButton
                                onClick={handleNotificationClick}
                                sx={{
                                    color: '#4B5563',
                                    position: 'relative',
                                    width: 36,
                                    height: 36,
                                }}
                            >
                                <NotificationsIcon />
                            </IconButton>
                            <ButtonBase
                                onClick={handleProfileMenuClick}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    borderRadius: 999,
                                    px: 1,
                                    py: 0.5,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 10px 28px rgba(15,23,42,0.15)',
                                    },
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: '#D1D5DB',
                                    }}
                                    src={userData?.profile_picture}
                                >
                                    {getUserInitials()}
                                </Avatar>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                    <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 16 }}>
                                        {getDisplayName()}
                                    </Typography>
                                    <Typography sx={{ color: '#6B7280', fontSize: 13, mt: '-4px' }}>
                                        {getUserTypeDisplay()}
                                    </Typography>
                                </Box>
                                <ArrowDownIcon sx={{ color: '#6B7280', width: 20, height: 20 }} />
                            </ButtonBase>
                        </Stack>

                        {/* 3. Period toggle (DOM last so it wraps to row 2 when width 100%; order 2 on md = between search and profile) */}
                        {isPerformanceRoute && (
                            <Box
                                sx={{
                                    width: { xs: '100%', md: 'auto' },
                                    minWidth: 0,
                                    order: { xs: 3, md: 2 },
                                    display: 'flex',
                                    justifyContent: { xs: 'flex-start', md: 'center' },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: 300,
                                        padding: '4px 6px',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        borderRadius: '8px',
                                        background: '#F4F4F6',
                                    }}
                                >
                                    {/* Month tab – clicking always opens the month picker */}
                                    <ButtonBase
                                        disabled={ytdDisabled}
                                        onClick={handleMonthTabClick}
                                        sx={{
                                            flex: 1,
                                            borderRadius: '6px',
                                            py: '4px',
                                            px: { xs: 1.5, sm: 2, md: 2.5 },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: !isYtd ? '#FFFFFF' : '#F4F4F6',
                                            boxShadow: !isYtd ? '0px 1px 2px 0px rgba(0,0,0,0.05)' : 'none',
                                            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                                                    fontSize: { xs: 13, sm: 14 },
                                                    fontWeight: !isYtd ? 600 : 500,
                                                    lineHeight: '20px',
                                                    color: '#262A2C',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {!isYtd ? selectedMonthLabel : 'Month'}
                                            </Typography>
                                            {/* Calendar chevron icon */}
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(0,0,0,0.45)" style={{ flexShrink: 0 }}>
                                                <path d="M7 10l5 5 5-5z" />
                                            </svg>
                                        </Stack>
                                    </ButtonBase>

                                    {/* Month picker popover */}
                                    <Popover
                                        open={monthPickerOpen}
                                        anchorEl={monthPickerAnchor}
                                        onClose={() => setMonthPickerOpen(false)}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                        PaperProps={{
                                            sx: {
                                                borderRadius: '10px',
                                                mt: 0.75,
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                                border: '1px solid rgba(0,0,0,0.08)',
                                            },
                                        }}
                                    >
                                        <MonthPickerContent
                                            selectedDate={selectedMonthDate}
                                            onSelect={handleMonthSelect}
                                        />
                                    </Popover>

                                    {/* Past 12 Months tab */}
                                    <ButtonBase
                                        disabled={ytdDisabled}
                                        onClick={() => handleYtdSelect(true)}
                                        sx={{
                                            flex: 1,
                                            borderRadius: '6px',
                                            py: '4px',
                                            px: { xs: 1.5, sm: 2, md: 2.5 },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: isYtd ? '#FFFFFF' : '#F4F4F6',
                                            boxShadow: isYtd ? '0px 1px 2px 0px rgba(0,0,0,0.05)' : 'none',
                                            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                                                fontSize: { xs: 13, sm: 14 },
                                                fontWeight: isYtd ? 600 : 500,
                                                lineHeight: '20px',
                                                color: '#262A2C',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Past 12 Months
                                        </Typography>
                                    </ButtonBase>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* Main Content */}
                    <Box component="main" sx={{ flexGrow: 1, p: 3, width: '100%', minWidth: 0 }}>
                        <Box
                            sx={{
                                maxWidth: contentMaxWidth,
                                width: '100%',
                                minWidth: 0,
                                ...(contentAlign === 'left'
                                    ? { mr: 'auto' }
                                    : contentAlign === 'right'
                                    ? { ml: 'auto' }
                                    : { mx: 'auto' }),
                            }}
                        >
                            {title && (
                                <Box
                                    sx={{
                                        pb: 2,
                                        mb: 2,
                                        borderBottom: '1px solid rgba(17, 24, 39, 0.08)',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: 'rgba(0, 0, 0, 0.87)',
                                            fontFeatureSettings: "'liga' off, 'clig' off",
                                            fontFamily: '"Rethink Sans", Helvetica, sans-serif',
                                            fontSize: '1.5rem',
                                            fontStyle: 'normal',
                                            fontWeight: 600,
                                            lineHeight: 1.334,
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                </Box>
                            )}
                            {children}
                        </Box>
                    </Box>
                </Box>

                {/* Notification Popover */}
                <Popover
                    open={notificationOpen}
                    anchorEl={notificationAnchorEl}
                    onClose={handleNotificationClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{ mt: 1 }}
                >
                    <Card sx={{ minWidth: 300 }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" align="center">
                                No new notifications
                            </Typography>
                        </CardContent>
                    </Card>
                </Popover>

                {/* Profile Menu */}
                <Menu
                    anchorEl={profileMenuAnchorEl}
                    open={profileMenuOpen}
                    onClose={handleProfileMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{ mt: 1 }}
                >
                    <MenuItem onClick={handleProfileSettings}>Profile Settings</MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>

                <SearchResultsModal
                    open={searchModalOpen}
                    onClose={handleCloseSearchModal}
                    isSearching={isSearching}
                    vendorResults={vendorResults}
                    categoryResults={categoryResults}
                    searchResults={searchResults}
                    searchQuery={modalSearchQuery}
                    onSearchQueryChange={setModalSearchQuery}
                    navigate={navigate}
                    fireToast={fireToast}
                />
            </Box>
        </ThemeProvider>
    );
}

// Search Results Modal Component
function SearchResultsModal({
    open,
    onClose,
    isSearching,
    vendorResults,
    categoryResults,
    searchResults,
    searchQuery,
    onSearchQueryChange,
    navigate,
    fireToast,
}) {
    const selectedRef = useRef(null);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const flatItems = useMemo(() => {
        const items = [];
        vendorResults.forEach((vendor) => {
            items.push({
                onClick: () => {
                    navigate(`/bank-activity?vendor=${encodeURIComponent(vendor.name)}`);
                    onClose();
                },
            });
        });
        categoryResults.forEach((category) => {
            items.push({
                onClick: () => {
                    navigate(`/bank-activity?category=${encodeURIComponent(category.name)}`);
                    onClose();
                },
            });
        });
        searchResults.forEach((transaction) => {
            const searchText = transaction.description || transaction.name || transaction.memo || '';
            items.push({
                onClick: () => {
                    if (searchText) {
                        navigate(`/bank-activity?search=${encodeURIComponent(searchText)}`);
                        onClose();
                    }
                },
            });
        });
        return items;
    }, [vendorResults, categoryResults, searchResults, navigate, onClose]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [searchQuery, flatItems.length]);

    useEffect(() => {
        if (selectedIndex >= 0 && selectedRef.current) {
            selectedRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selectedIndex]);

    const handleKeyDown = useCallback(
        (e) => {
            if (!open || flatItems.length === 0) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((i) => Math.min(i + 1, flatItems.length - 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((i) => Math.max(0, i - 1));
            } else if (e.key === 'Enter' && selectedIndex >= 0 && flatItems[selectedIndex]) {
                e.preventDefault();
                flatItems[selectedIndex].onClick();
            }
        },
        [open, flatItems, selectedIndex]
    );

    useEffect(() => {
        if (!open) return;
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, handleKeyDown]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="search-results-modal"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 650,
                    height: 450,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Modal Header with Search Input */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        height: 62,
                        px: 2.5,
                        borderBottom: '1px solid #E2E6EF',
                    }}
                >
                    <Box
                        component="img"
                        src="/search.svg"
                        alt=""
                        sx={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    <TextField
                        autoFocus
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => onSearchQueryChange(e.target.value)}
                        placeholder={SEARCH_INPUT_PLACEHOLDER}
                        size="small"
                        InputProps={{
                            endAdornment: searchQuery && (
                                <ButtonBase
                                    onClick={() => onSearchQueryChange('')}
                                    sx={{
                                        py: 0.5,
                                        px: 0.75,
                                        borderRadius: 1,
                                        color: '#6B7280',
                                        fontSize: 14,
                                        fontFamily: 'var(--fontFamily, "Rethink Sans")',
                                        '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                >
                                    Clear
                                </ButtonBase>
                            ),
                            sx: searchModalInputPropsSx,
                        }}
                        sx={{ minWidth: 0 }}
                    />
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ color: '#6B7280', flexShrink: 0 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Modal Content */}
                <Box sx={{ overflowY: 'auto', flexGrow: 1, p: 2.5 }}>
                    {isSearching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : !searchQuery.trim() ? (
                        // Empty state - show welcome screen
                        <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                                    textAlign: 'center',
                                    fontFeatureSettings: "'liga' off, 'clig' off",
                                    fontFamily: 'var(--fontFamily, "Rethink Sans")',
                                    fontSize: 'var(--font-size-125-rem, 20px)',
                                    fontStyle: 'normal',
                                    fontWeight: 'var(--fontWeightSemiBold, 600)',
                                    lineHeight: '160%',
                                    letterSpacing: '0.15px',
                                    mb: 2,
                                }}
                            >
                                What are you looking for?
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'var(--text-secondary, rgba(0, 0, 0, 0.60))',
                                    textAlign: 'center',
                                    fontFeatureSettings: "'liga' off, 'clig' off",
                                    fontFamily: 'var(--fontFamily, "Rethink Sans")',
                                    fontSize: 'var(--_fontSize-1rem, 16px)',
                                    fontStyle: 'normal',
                                    fontWeight: 'var(--fontWeightRegular, 400)',
                                    lineHeight: '150%',
                                    letterSpacing: '0.15px',
                                    mb: 4,
                                }}
                            >
                                Find pages, reports, vendors, and settings in seconds.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'nowrap',
                                    gap: 1,
                                    justifyContent: 'center',
                                    mt: 4,
                                }}
                            >
                                {[
                                    { label: 'Marketing', path: '/performance/marketing' },
                                    { label: 'Team', path: '/performance/team' },
                                ].map((category) => (
                                    <ButtonBase
                                        key={category.label}
                                        onClick={() => {
                                            if (category.path) {
                                                navigate(category.path);
                                                onClose();
                                            } else {
                                                fireToast(
                                                    "Scenario Planner",
                                                    "Under construction, coming soon....",
                                                    "info"
                                                );
                                            }
                                        }}
                                        sx={{
                                            display: 'flex',
                                            width: 104,
                                            padding: '4px 10px',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            borderRadius: 'var(--small, 4px)',
                                            background: 'var(--grey-100, #F5F5F5)',
                                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 2px 1px -1px rgba(0, 0, 0, 0.20)',
                                            transition: 'background 0.2s ease',
                                            '&:hover': {
                                                background: '#E8E8E8',
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                                                fontSize: 16,
                                                fontWeight: 500,
                                                color: '#111827',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {category.label}
                                        </Typography>
                                    </ButtonBase>
                                ))}
                            </Box>
                        </Box>
                    ) : (
                        <>
                            {/* Vendors Section */}
                            {vendorResults.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                                            fontWeight: 600,
                                            fontSize: 12,
                                            color: '#6B7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            mb: 1,
                                        }}
                                    >
                                        Vendors
                                    </Typography>
                                    <List sx={{ p: 0 }}>
                                        {vendorResults.map((vendor, index) => {
                                            const globalIndex = index;
                                            const isSelected = selectedIndex === globalIndex;
                                            return (
                                            <ListItem
                                                key={vendor.id || index}
                                                ref={isSelected ? selectedRef : undefined}
                                                component={ButtonBase}
                                                onClick={() => {
                                                    navigate(`/bank-activity?vendor=${encodeURIComponent(vendor.name)}`);
                                                    onClose();
                                                }}
                                                sx={{
                                                    borderRadius: 1,
                                                    py: 1.5,
                                                    px: 1,
                                                    textAlign: 'left',
                                                    width: '100%',
                                                    justifyContent: 'space-between',
                                                    bgcolor: isSelected ? '#F3F4F6' : 'transparent',
                                                    '&:hover': {
                                                        bgcolor: '#F3F4F6',
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                                                        fontSize: 15,
                                                        color: '#111827',
                                                    }}
                                                >
                                                    {vendor.name}
                                                </Typography>
                                                <NorthEastIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                                            </ListItem>
                                            );
                                        })}
                                    </List>
                                </Box>
                            )}

                            {/* Categories Section */}
                            {categoryResults.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                                            fontWeight: 600,
                                            fontSize: 12,
                                            color: '#6B7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            mb: 1,
                                        }}
                                    >
                                        Categories
                                    </Typography>
                                    <List sx={{ p: 0 }}>
                                        {categoryResults.map((category, index) => {
                                            const globalIndex = vendorResults.length + index;
                                            const isSelected = selectedIndex === globalIndex;
                                            return (
                                            <ListItem
                                                key={category.id || index}
                                                ref={isSelected ? selectedRef : undefined}
                                                component={ButtonBase}
                                                onClick={() => {
                                                    navigate(`/bank-activity?category=${encodeURIComponent(category.name)}`);
                                                    onClose();
                                                }}
                                                sx={{
                                                    borderRadius: 1,
                                                    py: 1.5,
                                                    px: 1,
                                                    textAlign: 'left',
                                                    width: '100%',
                                                    justifyContent: 'space-between',
                                                    bgcolor: isSelected ? '#F3F4F6' : 'transparent',
                                                    '&:hover': {
                                                        bgcolor: '#F3F4F6',
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                                                        fontSize: 15,
                                                        color: '#111827',
                                                    }}
                                                >
                                                    {category.name}
                                                </Typography>
                                                <NorthEastIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                                            </ListItem>
                                            );
                                        })}
                                    </List>
                                </Box>
                            )}

                            {/* Transactions Section */}
                            {searchResults.length > 0 && (
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                                            fontWeight: 600,
                                            fontSize: 12,
                                            color: '#6B7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            mb: 1,
                                        }}
                                    >
                                        Transactions
                                    </Typography>
                                    <List sx={{ p: 0 }}>
                                        {searchResults.map((transaction, index) => {
                                            const searchText = transaction.description || transaction.name || transaction.memo || '';
                                            const globalIndex = vendorResults.length + categoryResults.length + index;
                                            const isSelected = selectedIndex === globalIndex;
                                            return (
                                            <ListItem
                                                key={transaction.id || index}
                                                ref={isSelected ? selectedRef : undefined}
                                                component={ButtonBase}
                                                onClick={() => {
                                                    if (searchText) {
                                                        navigate(`/bank-activity?search=${encodeURIComponent(searchText)}`);
                                                        onClose();
                                                    }
                                                }}
                                                sx={{
                                                    borderRadius: 1,
                                                    py: 1.5,
                                                    px: 1,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    textAlign: 'left',
                                                    width: '100%',
                                                    bgcolor: isSelected ? '#F3F4F6' : 'transparent',
                                                    '&:hover': {
                                                        bgcolor: '#F3F4F6',
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                                                        fontSize: 15,
                                                        color: '#111827',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        flex: 1,
                                                        minWidth: 0,
                                                        mr: 2,
                                                    }}
                                                >
                                                    {transaction.description || transaction.name || transaction.memo || 'No description'}
                                                </Typography>
                                                <NorthEastIcon sx={{ fontSize: 18, color: '#9CA3AF', flexShrink: 0 }} />
                                            </ListItem>
                                        );
                                        })}
                                    </List>
                                </Box>
                            )}

                            {/* No Results */}
                            {vendorResults.length === 0 && categoryResults.length === 0 && searchResults.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                                            color: '#6B7280',
                                            fontSize: 14,
                                        }}
                                    >
                                        No results found for "{searchQuery}"
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>

                {/* Modal Footer */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 1.5,
                        borderTop: '1px solid #E2E6EF',
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        sx={{
                            color: 'var(--text-secondary, rgba(0, 0, 0, 0.60))',
                            fontFamily: 'var(--fontFamily, "Rethink Sans")',
                            fontSize: 12,
                            fontWeight: 400,
                        }}
                    >
                        Results update as you type
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'var(--text-secondary, rgba(0, 0, 0, 0.60))', fontSize: 12, fontFamily: 'var(--fontFamily, "Rethink Sans")' }}>
                        <span>Use</span>
                        <Box component="img" src="/search_up.svg" alt="Up" sx={{ width: 22, height: 22, verticalAlign: 'middle' }} />
                        <Box component="img" src="/search_down.svg" alt="Down" sx={{ width: 22, height: 22, verticalAlign: 'middle' }} />
                        <span>to navigate,</span>
                        <Box component="img" src="/enter.svg" alt="Enter" sx={{ height: 18, width: 'auto', verticalAlign: 'middle' }} />
                        <span>to open.</span>
                    </Stack>
                </Box>
            </Box>
        </Modal>
    );
}
